<template>
  <div class="manage-calendar-page">
    <div class="page-header">
      <h1>Manage Legislative Calendar</h1>
      <div class="header-controls">
        <div class="congress-selector">
          <label for="congress">Period of Congress:</label>
          <select id="congress" v-model="selectedCongress" @change="loadCalendar">
            <option value="20">20th Congress</option>
            <option value="19">19th Congress</option>
            <option value="18">18th Congress</option>
            <option value="17">17th Congress</option>
          </select>
        </div>
        <button class="add-btn" @click="openAddModal">+ Add Schedule</button>
      </div>
    </div>

    <div class="session-tabs">
      <button 
        class="tab-btn" 
        :class="{ active: selectedSession === '' }"
        @click="selectSession('')"
      >
        All Sessions
      </button>
      <button 
        class="tab-btn" 
        :class="{ active: selectedSession === 'First' }"
        @click="selectSession('First')"
      >
        First Session
      </button>
      <button 
        class="tab-btn" 
        :class="{ active: selectedSession === 'Second' }"
        @click="selectSession('Second')"
      >
        Second Session
      </button>
      <button 
        class="tab-btn" 
        :class="{ active: selectedSession === 'Third' }"
        @click="selectSession('Third')"
      >
        Third Session
      </button>
    </div>

    <div class="calendar-table">
      <table>
        <thead>
          <tr>
            <th>Congress No</th>
            <th>Session</th>
            <th>Schedule</th>
            <th>Date From</th>
            <th>Date To</th>
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in filteredCalendar" :key="entry.id">
            <td>{{ entry.congress_no }}</td>
            <td><span class="session-badge">{{ entry.session }}</span></td>
            <td><span class="schedule-badge" :class="getScheduleClass(entry.schedule)">{{ entry.schedule }}</span></td>
            <td>{{ formatDate(entry.date_from) }}</td>
            <td>{{ formatDate(entry.date_to) }}</td>
            <td>{{ formatDate(entry.created_at) }}</td>
            <td class="actions">
              <button class="edit-btn" @click="openEditModal(entry)">Edit</button>
              <button class="delete-btn" @click="deleteEntry(entry.id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ isEditing ? 'Edit Schedule' : 'Add Schedule' }}</h3>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <form @submit.prevent="saveEntry" class="modal-form">
          <div class="form-group">
            <label>Congress No:</label>
            <input v-model="form.congress_no" type="text" placeholder="e.g., 19" required>
          </div>
          <div class="form-group">
            <label>Session:</label>
            <select v-model="form.session" required>
              <option value="">Select Session</option>
              <option value="First">First</option>
              <option value="Second">Second</option>
              <option value="Third">Third</option>
            </select>
          </div>
          <div class="form-group">
            <label>Schedule:</label>
            <select v-model="form.schedule" required>
              <option value="">Select Schedule</option>
              <option value="Commencement of Session">Commencement of Session</option>
              <option value="Adjournment of Session">Adjournment of Session</option>
              <option value="Resumption of Session">Resumption of Session</option>
              <option value="Suspension of Session">Suspension of Session</option>
            </select>
          </div>
          <div class="form-group">
            <label>Date From:</label>
            <input v-model="form.date_from" type="date" required>
          </div>
          <div class="form-group">
            <label>Date To:</label>
            <input v-model="form.date_to" type="date" required>
          </div>
          <div class="modal-actions">
            <button type="button" class="cancel-btn" @click="closeModal">Cancel</button>
            <button type="submit" class="save-btn">{{ isEditing ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedCongress: '',
      selectedSession: '',
      calendar: [],
      showModal: false,
      isEditing: false,
      form: {
        id: null,
        congress_no: '',
        session: '',
        schedule: '',
        date_from: '',
        date_to: ''
      }
    }
  },
  computed: {
    filteredCalendar() {
      return this.calendar.filter(entry => {
        const sessionMatch = !this.selectedSession || entry.session === this.selectedSession
        return sessionMatch
      })
    }
  },
  methods: {
    async loadCalendar() {
      try {
        const response = await fetch(`https//:lmp.dof.gov.ph/api/calendar?congress_no=${this.selectedCongress}`)
        this.calendar = await response.json()
      } catch (error) {
        console.error('Error loading calendar:', error)
        this.calendar = []
      }
    },
    selectSession(session) {
      this.selectedSession = session
    },
    openAddModal() {
      this.isEditing = false
      this.form = { id: null, congress_no: this.selectedCongress, session: this.selectedSession, schedule: '', date_from: '', date_to: '' }
      this.showModal = true
    },
    openEditModal(entry) {
      this.isEditing = true
      this.form = { ...entry }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.form = { id: null, congress_no: this.selectedCongress, session: this.selectedSession, schedule: '', date_from: '', date_to: '' }
    },
    async saveEntry() {
      try {
        const data = {
          congress_no: this.form.congress_no,
          session: this.form.session,
          schedule: this.form.schedule,
          date_from: this.form.date_from,
          date_to: this.form.date_to
        }
        
        if (this.isEditing) {
          await fetch(`https//:lmp.dof.gov.ph/api/calendar/${this.form.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          })
        } else {
          await fetch('https//:lmp.dof.gov.ph/api/calendar', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          })
        }
        
        this.closeModal()
        this.loadCalendar()
      } catch (error) {
        console.error('Error saving entry:', error)
      }
    },
    async deleteEntry(id) {
      if (confirm('Are you sure you want to delete this entry?')) {
        try {
          await fetch(`https//:lmp.dof.gov.ph/api/calendar/${id}`, { method: 'DELETE' })
          this.loadCalendar()
        } catch (error) {
          console.error('Error deleting entry:', error)
        }
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },
    getScheduleClass(schedule) {
      const classes = {
        'Commencement of Session': 'commencement',
        'Adjournment of Session': 'adjournment',
        'Resumption of Session': 'resumption',
        'Suspension of Session': 'suspension'
      }
      return classes[schedule] || 'default'
    },
        async loadCurrentCongress() {
      try {
        const response = await fetch('https//:lmp.dof.gov.ph/api/current-congress')
        const current = await response.json()
        this.selectedCongress = current.congress_no
        this.selectedSession = current.session
      } catch (error) {
        this.selectedCongress = '19' // fallback
        this.selectedSession = 'First' // fallback
      }
    },

    },
  async mounted() {
    await this.loadCurrentCongress()
    this.loadCalendar()
  }
}
</script>

<style scoped>
.manage-calendar-page {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.add-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #1e40af;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.congress-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.congress-selector label {
  font-weight: 600;
  color: #1e3a8a;
}

.congress-selector select {
  padding: 0.5rem 1rem;
  border: 2px solid #1e3a8a;
  border-radius: 6px;
  background: white;
  color: #1e3a8a;
  font-weight: 500;
}

.session-tabs {
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
}

.tab-btn {
  background: none;
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #1e3a8a;
}

.tab-btn.active {
  color: #1e3a8a;
  border-bottom-color: #1e3a8a;
}

.calendar-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.1);
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  color: white;
}

th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
}

tbody tr {
  border-bottom: 1px solid #e5e7eb;
}

tbody tr:hover {
  background: #f8fafc;
}

td {
  padding: 1rem;
  color: #374151;
}

.schedule-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.schedule-badge.commencement {
  background: #d1fae5;
  color: #065f46;
}

.schedule-badge.adjournment {
  background: #fee2e2;
  color: #dc2626;
}

.schedule-badge.resumption {
  background: #dbeafe;
  color: #2563eb;
}

.schedule-badge.suspension {
  background: #fef3c7;
  color: #d97706;
}

.session-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #e0e7ff;
  color: #3730a3;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn, .delete-btn {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.edit-btn {
  background: #3b82f6;
  color: white;
}

.edit-btn:hover {
  background: #2563eb;
}

.delete-btn {
  background: #ef4444;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  color: #1e3a8a;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
}

.close-btn:hover {
  color: #374151;
}

.modal-form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #1e3a8a;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.3s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #1e3a8a;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.cancel-btn, .save-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.cancel-btn {
  background: #6b7280;
  color: white;
}

.cancel-btn:hover {
  background: #4b5563;
}

.save-btn {
  background: #1e3a8a;
  color: white;
}

.save-btn:hover {
  background: #1e40af;
}

@media (max-width: 768px) {
  .manage-calendar-page {
    padding: 1rem;
  }
  
  .calendar-table {
    overflow-x: auto;
  }
  
  table {
    min-width: 700px;
  }
  
  th, td {
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
  }
}
</style>